import { forwardRef } from 'react'
import {
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { IMaskInput, IMask } from 'react-imask'
import { useTranslation } from 'react-i18next'

export interface InterpretationRuleDurations {
    early_start_margin: string
    early_start_restriction: string
    late_start_restriction: string
    late_start_margin: string
    omitted_output_margin: string
    brake_maximum_output: string
    brake_minimum_refreshment_: string
    brake_early_rest_start: string
    brake_late_rest_start_margin: string
    brake_late_rest_end_margin: string
    start_early_end_restriction: string
    end_early_end_restriction: string
    late_end_restriction: string
    punch_out_limit_omitted: string
    unplanned_shift_duration: string
}

export interface InterpretationRuleFormData {
    name: string
    description: string
    punch_restriction: string
    allow_substitution: boolean | null
    brake_impose_reset: boolean | null
    restrict_unplanned_punch_in: boolean | null
    durations: InterpretationRuleDurations
}

type ShowErrorMap = Partial<Record<keyof InterpretationRuleDurations, boolean>>

interface InterpretationRuleFormProps {
    formData: InterpretationRuleFormData
    showError?: ShowErrorMap
    readOnly?: boolean
    nameErrorText?: string
    onSubmit?: () => void
    onNameChange?: (value: string) => void
    onDescriptionChange?: (value: string) => void
    onPunchRestrictionChange?: (value: string) => void
    onAllowSubstitutionChange?: (value: boolean) => void
    onBrakeImposeResetChange?: (value: boolean) => void
    onRestrictUnplannedPunchInChange?: (value: boolean) => void
    onDurationChange?: (key: keyof InterpretationRuleDurations, value: string) => void
}

interface TextMaskCustomProps {
    name?: string
    onChange: (event: { target: { name?: string; value: string } }) => void
}

const TextMaskCustom = forwardRef<HTMLInputElement, TextMaskCustomProps>((props, ref) => {
    return (
        <IMaskInput
            {...props}
            mask="HH:MM"
            definitions={{
                H: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 2,
                },
                M: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 5,
                },
            }}
            blocks={{
                HH: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 24,
                    maxLength: 2,
                },
                MM: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 59,
                    maxLength: 2,
                },
            }}
            prepare={(value: string) => {
                const parts = value.split(':')
                if (parts.length === 2) {
                    const hours = parseInt(parts[0], 10)
                    const minutes = parseInt(parts[1], 10)

                    if (hours === 24 && minutes > 0) {
                        return '24:00'
                    }
                }
                return value
            }}
            validate={(value: string) => {
                const parts = value.split(':')
                if (parts.length === 2) {
                    const hours = parseInt(parts[0], 10)
                    const minutes = parseInt(parts[1], 10)

                    if (hours === 24 && minutes > 0) {
                        return false
                    }
                }
                return true
            }}
            placeholder={'24:00'}
            inputRef={ref}
            onAccept={(value: string) => props.onChange({ target: { name: props.name, value } })}
            overwrite
        />
    )
})

interface TimeFieldProps {
    id: keyof InterpretationRuleDurations
    label: string
    value: string
    onChange: (value: string) => void
    error?: boolean
    readOnly: boolean
    marginTop?: boolean
}

const TimeField = ({
    id,
    label,
    value,
    onChange,
    error,
    readOnly,
    marginTop,
}: TimeFieldProps) => {
    return (
        <TextField
            id={id}
            name={id}
            fullWidth
            autoComplete='off'
            sx={marginTop ? { mt: 1.5 } : undefined}
            label={label}
            onChange={readOnly ? undefined : (e) => onChange(e.target.value)}
            value={value}
            disabled={readOnly}
            slotProps={
                readOnly
                    ? undefined
                    : {
                        input: {
                            inputComponent: TextMaskCustom,
                            endAdornment: error ? (
                                <InputAdornment position='end' style={{ cursor: 'pointer' }} color='error'>
                                    <Tooltip title='Formato HH:MM' arrow>
                                        <InfoIcon color='error' />
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                        },
                    }
            }
            required
            error={!readOnly && Boolean(error)}
        />
    )
}

const InterpretationRuleForm = ({
    formData,
    showError = {},
    readOnly = false,
    nameErrorText = '',
    onSubmit,
    onNameChange,
    onDescriptionChange,
    onPunchRestrictionChange,
    onAllowSubstitutionChange,
    onBrakeImposeResetChange,
    onRestrictUnplannedPunchInChange,
    onDurationChange,
}: InterpretationRuleFormProps) => {
    const { t } = useTranslation()

    const {
        name,
        description,
        punch_restriction,
        allow_substitution,
        brake_impose_reset,
        restrict_unplanned_punch_in,
        durations,
    } = formData

    return (
        <Box
            component='form'
            onSubmit={(event) => {
                event.preventDefault()
                if (!readOnly && onSubmit) {
                    onSubmit()
                }
            }}
        >
            <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ mt: 1.5 }}>
                    <TextField
                        id='name'
                        name='name'
                        fullWidth
                        label={t('settings.interpretation_rule.dialog.inputs.name')}
                        value={name}
                        autoComplete='off'
                        onChange={readOnly ? undefined : (e) => onNameChange?.(e.target.value)}
                        required
                        disabled={readOnly}
                        error={!readOnly && Boolean(nameErrorText)}
                        slotProps={{
                            htmlInput: { maxLength: 100 },
                            input: {
                                endAdornment: (
                                    <InputAdornment position='end'>{`${name.length}/100`}</InputAdornment>
                                ),
                            },
                        }}
                    />
                    {!readOnly && (
                        <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }}>
                            <span>{nameErrorText}</span>
                        </FormHelperText>
                    )}
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }} sx={{ mt: 1.5 }}>
                    <FormControl fullWidth>
                        <InputLabel id='interpretation-rule-punch-restriction-label'>
                            {t('settings.interpretation_rule.dialog.inputs.punch_restriction')}
                        </InputLabel>
                        <Select
                            id='punch_restriction'
                            name='punch_restriction'
                            labelId='interpretation-rule-punch-restriction-label'
                            label={t('settings.interpretation_rule.dialog.inputs.punch_restriction')}
                            value={punch_restriction}
                            onChange={readOnly ? undefined : (e) => onPunchRestrictionChange?.(e.target.value)}
                            disabled={readOnly}
                        >
                            <MenuItem value='simple'>{t('settings.interpretation_rule.dialog.inputs.simple')}</MenuItem>
                            <MenuItem value='complete'>{t('settings.interpretation_rule.dialog.inputs.complete')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                    <Typography variant='body1'>
                        {t('settings.interpretation_rule.dialog.inputs.allow_substitution')}
                    </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <RadioGroup
                        id='allow_substitution'
                        name='allow_substitution'
                        value={String(allow_substitution ?? false)}
                        onChange={readOnly ? undefined : (e) => onAllowSubstitutionChange?.(e.target.value === 'true')}
                        row
                    >
                        <FormControlLabel value='true' control={<Radio />} label={'Si'} disabled={readOnly} />
                        <FormControlLabel value='false' control={<Radio />} label={'No'} disabled={readOnly} />
                    </RadioGroup>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                    <Typography variant='body1'>
                        {t('settings.interpretation_rule.dialog.inputs.plan_start_time')}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <TimeField
                        id='early_start_margin'
                        label={t('settings.interpretation_rule.dialog.inputs.early_start_margin')}
                        value={durations.early_start_margin}
                        onChange={(value) => onDurationChange?.('early_start_margin', value)}
                        error={showError.early_start_margin}
                        readOnly={readOnly}
                    />
                </Grid>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <TimeField
                        id='early_start_restriction'
                        label={t('settings.interpretation_rule.dialog.inputs.early_start_restriction')}
                        value={durations.early_start_restriction}
                        onChange={(value) => onDurationChange?.('early_start_restriction', value)}
                        error={showError.early_start_restriction}
                        readOnly={readOnly}
                    />
                </Grid>

                {punch_restriction === 'complete' && (
                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <TimeField
                            id='late_start_restriction'
                            label={t('settings.interpretation_rule.dialog.inputs.late_start_restriction')}
                            value={durations.late_start_restriction}
                            onChange={(value) => onDurationChange?.('late_start_restriction', value)}
                            error={showError.late_start_restriction}
                            readOnly={readOnly}
                        />
                    </Grid>
                )}

                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <TimeField
                        id='late_start_margin'
                        label={t('settings.interpretation_rule.dialog.inputs.late_start_margin')}
                        value={durations.late_start_margin}
                        onChange={(value) => onDurationChange?.('late_start_margin', value)}
                        error={showError.late_start_margin}
                        readOnly={readOnly}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                    <Typography variant='body1'>
                        {t('settings.interpretation_rule.dialog.inputs.omitted_input')}
                    </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <TimeField
                        id='omitted_output_margin'
                        label={t('settings.interpretation_rule.dialog.inputs.omitted_input_margin')}
                        value={durations.omitted_output_margin}
                        onChange={(value) => onDurationChange?.('omitted_output_margin', value)}
                        error={showError.omitted_output_margin}
                        readOnly={readOnly}
                    />
                </Grid>

                {punch_restriction === 'complete' && (
                    <>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                            <Typography variant='body1'>
                                {t('settings.interpretation_rule.dialog.inputs.rests')}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TimeField
                                id='brake_maximum_output'
                                label={t('settings.interpretation_rule.dialog.inputs.brake_maximum_output')}
                                value={durations.brake_maximum_output}
                                onChange={(value) => onDurationChange?.('brake_maximum_output', value)}
                                error={showError.brake_maximum_output}
                                readOnly={readOnly}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TimeField
                                id='brake_minimum_refreshment_'
                                label={t('settings.interpretation_rule.dialog.inputs.brake_minimum_refreshment_')}
                                value={durations.brake_minimum_refreshment_}
                                onChange={(value) => onDurationChange?.('brake_minimum_refreshment_', value)}
                                error={showError.brake_minimum_refreshment_}
                                readOnly={readOnly}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <FormLabel variant='body1'>
                                {t('settings.interpretation_rule.dialog.inputs.brake_impose_reset')}
                            </FormLabel>
                            <RadioGroup
                                id='brake_impose_reset'
                                name='brake_impose_reset'
                                value={String(brake_impose_reset ?? false)}
                                onChange={readOnly ? undefined : (e) => onBrakeImposeResetChange?.(e.target.value === 'true')}
                                row
                            >
                                <FormControlLabel value='true' control={<Radio />} label={'Si'} disabled={readOnly} />
                                <FormControlLabel value='false' control={<Radio />} label={'No'} disabled={readOnly} />
                            </RadioGroup>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TimeField
                                id='brake_early_rest_start'
                                label={t('settings.interpretation_rule.dialog.inputs.brake_early_rest_start')}
                                value={durations.brake_early_rest_start}
                                onChange={(value) => onDurationChange?.('brake_early_rest_start', value)}
                                error={showError.brake_early_rest_start}
                                readOnly={readOnly}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TimeField
                                id='brake_late_rest_start_margin'
                                label={t('settings.interpretation_rule.dialog.inputs.brake_late_rest_start_margin')}
                                value={durations.brake_late_rest_start_margin}
                                onChange={(value) => onDurationChange?.('brake_late_rest_start_margin', value)}
                                error={showError.brake_late_rest_start_margin}
                                readOnly={readOnly}
                                marginTop
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TimeField
                                id='brake_late_rest_end_margin'
                                label={t('settings.interpretation_rule.dialog.inputs.brake_late_rest_end_margin')}
                                value={durations.brake_late_rest_end_margin}
                                onChange={(value) => onDurationChange?.('brake_late_rest_end_margin', value)}
                                error={showError.brake_late_rest_end_margin}
                                readOnly={readOnly}
                                marginTop
                            />
                        </Grid>
                    </>
                )}

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                    <Typography variant='body1'>
                        {t('settings.interpretation_rule.dialog.inputs.plan_end_time')}
                    </Typography>
                </Grid>

                {punch_restriction === 'complete' && (
                    <>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TimeField
                                id='start_early_end_restriction'
                                label={t('settings.interpretation_rule.dialog.inputs.start_early_end_restriction')}
                                value={durations.start_early_end_restriction}
                                onChange={(value) => onDurationChange?.('start_early_end_restriction', value)}
                                error={showError.start_early_end_restriction}
                                readOnly={readOnly}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <TimeField
                                id='end_early_end_restriction'
                                label={t('settings.interpretation_rule.dialog.inputs.end_early_end_restriction')}
                                value={durations.end_early_end_restriction}
                                onChange={(value) => onDurationChange?.('end_early_end_restriction', value)}
                                error={showError.end_early_end_restriction}
                                readOnly={readOnly}
                            />
                        </Grid>
                    </>
                )}

                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <TimeField
                        id='late_end_restriction'
                        label={t('settings.interpretation_rule.dialog.inputs.late_end_restriction')}
                        value={durations.late_end_restriction}
                        onChange={(value) => onDurationChange?.('late_end_restriction', value)}
                        error={showError.late_end_restriction}
                        readOnly={readOnly}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                    <Typography variant='body1'>
                        {t('settings.interpretation_rule.dialog.inputs.omitted_output')}
                    </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <TimeField
                        id='punch_out_limit_omitted'
                        label={t('settings.interpretation_rule.dialog.inputs.punch_out_limit_omitted')}
                        value={durations.punch_out_limit_omitted}
                        onChange={(value) => onDurationChange?.('punch_out_limit_omitted', value)}
                        error={showError.punch_out_limit_omitted}
                        readOnly={readOnly}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                    <Typography variant='body1'>
                        {t('settings.interpretation_rule.dialog.inputs.not_planning')}
                    </Typography>
                </Grid>
                {punch_restriction === 'complete' && (
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <FormLabel variant='body1'>
                            {t('settings.interpretation_rule.dialog.inputs.restrict_unplanned_punch_in')}
                        </FormLabel>
                        <RadioGroup
                            id='restrict_unplanned_punch_in'
                            name='restrict_unplanned_punch_in'
                            value={String(restrict_unplanned_punch_in ?? false)}
                            onChange={readOnly ? undefined : (e) => onRestrictUnplannedPunchInChange?.(e.target.value === 'true')}
                            row
                        >
                            <FormControlLabel value='true' control={<Radio />} label={'Si'} disabled={readOnly} />
                            <FormControlLabel value='false' control={<Radio />} label={'No'} disabled={readOnly} />
                        </RadioGroup>
                    </Grid>
                )}

                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <TimeField
                        id='unplanned_shift_duration'
                        label={t('settings.interpretation_rule.dialog.inputs.unplanned_shift_duration')}
                        value={durations.unplanned_shift_duration}
                        onChange={(value) => onDurationChange?.('unplanned_shift_duration', value)}
                        error={showError.unplanned_shift_duration}
                        readOnly={readOnly}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <TextField
                        id='description'
                        name='description'
                        fullWidth
                        label={t('settings.interpretation_rule.dialog.inputs.description')}
                        onChange={readOnly ? undefined : (e) => onDescriptionChange?.(e.target.value)}
                        value={description}
                        autoComplete='off'
                        multiline
                        rows={readOnly ? 4 : 2}
                        disabled={readOnly}
                        slotProps={
                            readOnly
                                ? undefined
                                : {
                                    htmlInput: { maxLength: 255 },
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position='end'>{`${description.length}/255`}</InputAdornment>
                                        ),
                                    },
                                }
                        }
                        placeholder={
                            readOnly
                                ? undefined
                                : t('settings.interpretation_rule.dialog.inputs.max_description')
                        }
                    />
                </Grid>
            </Grid>
        </Box>
    )
}

export { InterpretationRuleForm }